import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { YMaps, Map, Placemark, ZoomControl, FullscreenControl } from '@pbe/react-yandex-maps';
import { useCart } from '../context/CartContext';
import { apiService } from '../services/api';
import { isCafeInactive, escapeHtml } from '../utils/coffeeShop';
import { getWorkingHoursText } from '../utils/workingHours';
import NotificationBell from './NotificationBell';
import '../styles/MapView.css';

// Попробуем использовать геолокацию, если доступна, иначе заданный центр
const defaultCenter = [55.980201, 37.187689];
const defaultZoom = 13;

const MapView = () => {
  const navigate = useNavigate();
  const { setSelectedCafe, carts } = useCart();
  const [selectedCafe, setSelectedCafeLocal] = useState(null);
  
  // Подсчет общего количества товаров во всех корзинах
  const totalCartItems = useMemo(() => {
    return Object.values(carts).reduce((total, cart) => {
      return total + (cart.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0);
    }, 0);
  }, [carts]);
  const [coffeeShops, setCoffeeShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(defaultZoom);
  const [mapReady, setMapReady] = useState(false);
  const [userLocationReceived, setUserLocationReceived] = useState(false);
  const [userLocation, setUserLocation] = useState(null); // Координаты пользователя
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const mapRef = useRef(null); // ссылка на карту
  const placemarkRefs = useRef([]);
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);

  // Функция для центрирования карты на координатах (используем useCallback для стабильности)
  const centerMapOn = React.useCallback((coords, zoom = 14) => {
    if (mapRef.current && mapReady) {
      try {
        mapRef.current.setCenter(coords, zoom, {
          duration: 500
        });
        console.log('Карта центрирована на пользователе:', coords);
      } catch (error) {
        console.error('Ошибка при центрировании карты:', error);
        // Повторная попытка через небольшую задержку
        setTimeout(() => {
          if (mapRef.current) {
            try {
              mapRef.current.setCenter(coords, zoom, {
                duration: 500
              });
              console.log('Карта центрирована (повторная попытка):', coords);
            } catch (retryError) {
              console.error('Ошибка при повторной попытке центрирования:', retryError);
            }
          }
        }, 300);
      }
    } else {
      console.warn('Карта еще не готова для центрирования');
    }
  }, [mapReady]);

  // Автоматическое определение местоположения при загрузке компонента
  useEffect(() => {
    if (!navigator.geolocation) {
      console.warn('Геолокация не поддерживается браузером');
      return;
    }

    console.log('Запрос геолокации...');
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userCoords = [position.coords.latitude, position.coords.longitude];
        console.log('Геолокация получена:', userCoords);
        
        setUserLocation(userCoords);
        setUserLocationReceived(true);
        setMapCenter(userCoords);
        setMapZoom(14);
        
        // Немедленно пытаемся центрировать карту, даже если она еще не готова
        // useEffect также сработает когда карта будет готова
      },
      (error) => {
        console.warn('Ошибка получения геолокации:', error.code, error.message);
        setUserLocationReceived(false);
        
        // Используем центр по умолчанию
        setMapCenter(defaultCenter);
        setMapZoom(defaultZoom);
        
        // Показываем понятное сообщение пользователю
        if (error.code === 1) {
          console.warn('Пользователь отклонил запрос геолокации');
        } else if (error.code === 2) {
          console.warn('Местоположение недоступно');
        } else if (error.code === 3) {
          console.warn('Превышено время ожидания геолокации');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000, // Таймаут 10 секунд
        maximumAge: 60000 // Используем кешированное местоположение до 1 минуты
      }
    );
  }, []);

  // Обновление центра карты при изменении mapCenter или mapZoom (после загрузки карты)
  useEffect(() => {
    // Центрируем карту если:
    // 1. Карта готова
    // 2. Получена геолокация пользователя
    // 3. Есть координаты пользователя
    if (mapReady && userLocationReceived && userLocation) {
      console.log('Центрируем карту на пользователе...', userLocation);
      centerMapOn(userLocation, 14);
    }
  }, [mapReady, userLocationReceived, userLocation, centerMapOn]);

  // Обработчик загрузки карты
  const handleMapLoad = () => {
    console.log('Карта загружена и готова');
    setMapReady(true);
    
    // Если уже есть координаты пользователя, обязательно центрируем карту
    if (userLocationReceived && userLocation) {
      console.log('Центрируем карту на пользователе при загрузке:', userLocation);
      // Даем карте немного времени на инициализацию
      setTimeout(() => {
        centerMapOn(userLocation, 14);
      }, 300);
      
      // Дополнительная попытка через секунду на случай если первая не сработала
      setTimeout(() => {
        if (mapRef.current && userLocation) {
          try {
            mapRef.current.setCenter(userLocation, 14, {
              duration: 500
            });
            console.log('Карта центрирована (дополнительная попытка)');
          } catch (error) {
            console.error('Ошибка при дополнительной попытке центрирования:', error);
          }
        }
      }, 1500);
    }
  };

  // Загрузка данных кофеен при монтировании компонента
  useEffect(() => {
    const loadCoffeeShops = async () => {
      try {
        setLoading(true);
        setError(null);
        const shops = await apiService.getCoffeeShops();
        setCoffeeShops(shops);
        placemarkRefs.current = shops.map(() => React.createRef());
      } catch (err) {
        setError(err.message);
        console.error('Ошибка загрузки кофеен:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCoffeeShops();
  }, []);

  useEffect(() => {
    const n = coffeeShops.length;
    if (placemarkRefs.current.length !== n) {
      placemarkRefs.current = coffeeShops.map((_, i) =>
        placemarkRefs.current[i] ?? React.createRef()
      );
    }
  }, [coffeeShops]);

  // Поиск кофеен с debounce (задержка 500ms)
  useEffect(() => {
    // Очищаем предыдущий таймаут
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Если поисковая строка пустая, очищаем результаты
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      setIsSearching(false);
      return;
    }

    // Устанавливаем задержку 500ms перед отправкой запроса
    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await apiService.searchCoffeeShops(searchQuery, 10);
        setSearchResults(results);
        setShowSearchResults(true);
      } catch (err) {
        console.error('Ошибка поиска:', err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 500);

    // Очистка таймаута при размонтировании
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Обработчик изменения поискового запроса
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (!value.trim()) {
      setShowSearchResults(false);
    }
  };

  // Обработчик выбора результата поиска
  const handleSearchResultClick = (shop) => {
    setSearchQuery(shop.name);
    setShowSearchResults(false);
    
    // Находим индекс кофейни в основном списке
    const index = coffeeShops.findIndex(c => c.id === shop.id);
    
    if (index !== -1) {
      // Кофейня уже есть на карте
      setSelectedCafeLocal(shop);
      setSelectedCafe(shop);
      
      // Центрируем карту на кофейне
      if (mapRef.current) {
        mapRef.current.setCenter(shop.coordinates, 15, {
          duration: 500
        });
      }
      
      // Открываем балун
      setTimeout(() => {
        const placemark = placemarkRefs.current[index]?.current;
        if (placemark && placemark.balloon) {
          placemark.balloon.open();
        }
      }, 600);
    } else {
      // Если кофейня не найдена в основном списке, добавляем её временно
      setSelectedCafeLocal(shop);
      setSelectedCafe(shop);
      
      if (mapRef.current) {
        mapRef.current.setCenter(shop.coordinates, 15, {
          duration: 500
        });
      }
    }
    
    // Убираем фокус с поля ввода
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
  };

  // Обработчик закрытия результатов поиска при клике вне области
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSearchResults && !event.target.closest('.search-container')) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchResults]);

  const handleCafeSelect = (shop, index) => {
    setSelectedCafeLocal(shop);
    setSelectedCafe(shop);

    setTimeout(() => {
      const placemark = placemarkRefs.current[index]?.current;
      if (placemark && placemark.balloon) {
        placemark.balloon.open();
      }
    }, 0);
  };

  const handleOpenMenuForSelected = () => {
    if (selectedCafe) {
      navigate(`/menu/${selectedCafe.id}`);
    }
  };

  const handleMapClick = (event) => {
    // если кликнули не на маркер, сбрасываем выбор
    if (!event.get('target').geometry) {
      setSelectedCafeLocal(null);
      setSelectedCafe(null);
    }
  };

  const handleNearbyClick = () => {
    if (!navigator.geolocation) {
      alert('Геолокация не поддерживается вашим браузером');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userCoords = [position.coords.latitude, position.coords.longitude];
        
        // Обновляем центр карты на местоположение пользователя
        setMapCenter(userCoords);
        setMapZoom(14);
        if (mapRef.current) {
          mapRef.current.setCenter(userCoords, 14, {
            duration: 500
          });
        }

        // Находим ближайшую кофейню
        let closestCafe = null;
        let minDistance = Infinity;

        coffeeShops.forEach((shop) => {
          const distance = getDistance(userCoords, shop.coordinates);
          if (distance < minDistance) {
            minDistance = distance;
            closestCafe = shop;
          }
        });

        if (closestCafe) {
          const index = coffeeShops.findIndex(cafe => cafe.id === closestCafe.id);
          setSelectedCafeLocal(closestCafe);
          setSelectedCafe(closestCafe);

          // Центрируем карту на ближайшую кофейню с небольшой задержкой
          setTimeout(() => {
            if (mapRef.current) {
              mapRef.current.setCenter(closestCafe.coordinates, 14, {
                duration: 500
              });
            }
          }, 600);

          // Открываем балун
          setTimeout(() => {
            const placemark = placemarkRefs.current[index]?.current;
            if (placemark && placemark.balloon) {
              placemark.balloon.open();
            }
          }, 1000);
        }
      },
      (error) => {
        alert('Не удалось определить ваше местоположение');
        console.error(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );
  };

  // Функция для расчета расстояния между двумя координатами (Haversine formula)
  function getDistance(coords1, coords2) {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const [lat1, lon1] = coords1;
    const [lat2, lon2] = coords2;

    const R = 6371; // радиус Земли в км
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // расстояние в км
  };


  // Если идет загрузка
  if (loading) {
    return (
      <div className="map-container">
        <div className="header">
          <div className="flex space-between align-center">
            <div>
              <h1>RCoffee</h1>
              <p className="subtitle">Загрузка кофеен...</p>
            </div>
          </div>
        </div>
        <div className="map-loading-container">
          <div>Загружаем список кофеен...</div>
        </div>
      </div>
    );
  }

  // Если произошла ошибка
  if (error) {
    return (
      <div className="map-container">
        <div className="header">
          <div className="flex space-between align-center">
            <div>
              <h1>RCoffee</h1>
              <p className="subtitle">Ошибка загрузки</p>
            </div>
          </div>
        </div>
        <div className="map-error-container">
          <div className="map-error-icon"></div>
          <div className="map-error-message">{error}</div>
          <button 
            className="btn map-error-button"
            onClick={() => window.location.reload()}
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="map-container">
      <div className="header">
        <div className="flex space-between align-center">
          <h1 style={{ margin: 0 }}>RCoffee</h1>
          <div className="map-header-buttons">
            <NotificationBell />
            <button
              className="btn map-header-button"
              onClick={() => navigate('/profile')}
            >
              Профиль
            </button>
            <button
              className="btn map-header-button"
              onClick={() => navigate('/orders')}
            >
              Заказы
            </button>
          </div>
        </div>
        
        {/* Строка поиска */}
        <div className="search-container map-search-container">
          <div className="map-search-wrapper">
            <input
              ref={searchInputRef}
              type="text"
              className="search-input map-search-input"
              placeholder="Поиск кофеен по названию или адресу..."
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => {
                if (searchResults.length > 0) {
                  setShowSearchResults(true);
                }
              }}
            />
            <span className="map-search-icon">
            </span>
            {isSearching && (
              <span className="map-search-loading">
                Поиск...
              </span>
            )}
          </div>

          {/* Результаты поиска */}
          {showSearchResults && searchResults.length > 0 && (
            <div className="search-results map-search-results">
              {searchResults.map((shop) => (
                <div
                  key={shop.id}
                  className="search-result-item map-search-result-item"
                  onClick={() => handleSearchResultClick(shop)}
                >
                  <div className="map-search-result-icon"></div>
                  <div className="map-search-result-content">
                    <div className="map-search-result-name">
                      <span>{shop.name}</span>
                      {isCafeInactive(shop) && (
                        <span className="map-search-soon-badge">Скоро</span>
                      )}
                    </div>
                    <div className="map-search-result-address">
                      {shop.address}
                    </div>
                    {shop.rating && (
                      <div className="map-search-result-rating">
                        ⭐ {shop.rating}
                      </div>
                    )}
                  </div>
                  <div className="map-search-result-arrow">
                    →
                  </div>
                </div>
              ))}
            </div>
          )}

          {showSearchResults && searchQuery.trim() && searchResults.length === 0 && !isSearching && (
            <div className="map-search-empty">
              Ничего не найдено
            </div>
          )}
        </div>
      </div>

      <YMaps query={{ lang: 'ru_RU', apikey: 'ffaa4605-e921-4154-98a3-237c53fb4ef5' }}>
        <Map
          defaultState={{ center: mapCenter, zoom: mapZoom }}
          width="100%"
          height="100vh"
          instanceRef={mapRef}
          onClick={handleMapClick}
          onLoad={handleMapLoad}
        >
          <ZoomControl />
          <FullscreenControl />
          
          {/* Маркер местоположения пользователя */}
          {userLocation && (
            <Placemark
              geometry={userLocation}
              options={{
                preset: 'islands#blueCircleDotIcon',
                iconColor: '#3b82f6',
                zIndex: 1000
              }}
              properties={{
                balloonContentHeader: '<strong>Ваше местоположение</strong>',
                balloonContentBody: `
                  <div style="white-space: normal; max-width: 250px;">
                    <div>Вы здесь</div>
                  </div>
                `,
              }}
            />
          )}
          
          {coffeeShops.map((shop, index) => {
            const workingHoursText = getWorkingHoursText(shop);
            const inactive = isCafeInactive(shop);
            const safeName = escapeHtml(shop.name);
            const safeAddress = escapeHtml(shop.address);
            const safeHours = escapeHtml(workingHoursText);
            const ratingStr =
              shop.rating != null && shop.rating !== ''
                ? escapeHtml(String(shop.rating))
                : '—';
            const distanceStr =
              shop.distance != null && shop.distance !== ''
                ? escapeHtml(String(shop.distance))
                : '—';
            const soonHtml = inactive
              ? `<div style="margin-top:10px;padding:10px 12px;background:rgba(255,193,7,0.2);border-radius:10px;font-size:13px;color:#5d4037;border:1px solid rgba(255,152,0,0.35);"><strong>Скоро</strong><br/><span style="font-size:12px;line-height:1.35;">Заказ пока недоступен. Меню можно посмотреть.</span></div>`
              : '';
            return (
              <Placemark
                key={shop.id}
                geometry={shop.coordinates}
                instanceRef={placemarkRefs.current[index]}
                onClick={() => handleCafeSelect(shop, index)}
                options={{
                  preset: 'islands#brownCoffeeIcon',
                  iconCaptionMaxWidth: '180',
                }}
                properties={{
                  iconCaption: shop.name,
                  balloonContentHeader: inactive
                    ? `<strong>${safeName}</strong> &nbsp;<span style="font-size:11px;font-weight:700;letter-spacing:0.05em;text-transform:uppercase;color:#5d4037;background:rgba(255,193,7,0.45);padding:2px 8px;border-radius:999px;border:1px solid rgba(255,152,0,0.5);">Скоро</span>`
                    : `<strong>${safeName}</strong>`,
                  balloonContentBody: `
                    <div style="white-space: normal; max-width: 250px;">
                      <div>${safeAddress}</div>
                      <div>🕐 ${safeHours}</div>
                      <div>⭐ ${ratingStr} • ${distanceStr}</div>
                      ${soonHtml}
                    </div>
                  `,
                }}
              />
            );
          })}
        </Map>
      </YMaps>

      <div className="nearby-button-container">
        <button className="nearby-button" onClick={handleNearbyClick}>
          Рядом со мной
        </button>
      </div>

      <button
        className="map-floating-cart-btn"
        onClick={() => navigate('/cart')}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="white" viewBox="0 0 16 16">
          <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
        </svg>
        {totalCartItems > 0 && (
          <span className="map-cart-badge">
            {totalCartItems > 99 ? '99+' : totalCartItems}
          </span>
        )}
      </button>

      {selectedCafe && (
        <div className="map-info">
          <div className="map-info-title-row">
            <h3>{selectedCafe.name}</h3>
            {isCafeInactive(selectedCafe) && (
              <span className="map-soon-badge">Скоро</span>
            )}
          </div>
          <p>{selectedCafe.address}</p>
          <p>
            🕐 {getWorkingHoursText(selectedCafe)} • ⭐ {selectedCafe.rating} •{' '}
            {selectedCafe.distance}
          </p>
          <button className="btn" type="button" onClick={handleOpenMenuForSelected}>
            {isCafeInactive(selectedCafe) ? 'Меню' : 'Заказать здесь'}
          </button>
        </div>
      )}
    </div>
  );
};

export default MapView;
